/**
 * Invoice record interface and related types
 */

/**
 * @typedef {Object} InvoiceRecord
 * @property {number} id - Unique identifier for the record
 * @property {string | null} distCode - Distributor code
 * @property {string} invNo - Invoice number
 * @property {string | null} createDate - Creation date in ISO format
 * @property {string | null} invoiceDate - Invoice date in ISO format
 * @property {string | null} warehouseCode - Warehouse code
 * @property {string} customerCode - Customer code/ID
 * @property {string | null} salesmanCode - Salesman code
 * @property {string} productCode - Product code
 * @property {string} productType - Product type (e.g., 'Selling')
 * @property {number | string | null} uomConvFactor - Unit of measure conversion factor
 * @property {string | null} uomCode - Unit of measure code
 * @property {number | string | null} quantity - Quantity of the product
 * @property {number | string | null} netAmount - Net amount before tax and discounts
 * @property {number | string | null} promoDiscount - Promotional discount amount
 * @property {number | string | null} vatTaxAmount - VAT/tax amount
 * @property {number | string | null} totalNetAmount - Total net amount
 * @property {boolean} isActive - If the record is active
 * @property {string} createdAt - Record creation timestamp
 * @property {string | null} createdBy - Who created the record
 * @property {string | null} updatedBy - Who last updated the record
 * @property {string} updatedAt - Record last update timestamp
 */

/**
 * @typedef {Object} InvoiceLineItem
 * @property {string} id - Line item ID
 * @property {string} productCode - Product/service code
 * @property {string} description - Item description
 * @property {number} quantity - Quantity
 * @property {number} unitPrice - Price per unit
 * @property {number} lineTotal - Total for this line item
 * @property {number} taxRate - Tax rate for this item
 * @property {number} taxAmount - Tax amount for this item
 */

/**
 * @typedef {'valid' | 'invalid' | 'pending' | 'processing'} ValidationStatus
 */

/**
 * @typedef {Object} FileProcessingResult
 * @property {boolean} success - Whether processing was successful
 * @property {InvoiceRecord[]} records - Parsed invoice records
 * @property {number} totalRecords - Total number of records processed
 * @property {number} validRecords - Number of valid records
 * @property {number} invalidRecords - Number of invalid records
 * @property {ProcessingError[]} errors - Array of processing errors
 * @property {Object} metadata - Processing metadata
 */

/**
 * @typedef {Object} ProcessingError
 * @property {number} row - Row number where error occurred
 * @property {string} field - Field name with error
 * @property {string} message - Error message
 * @property {string} value - Original value that caused error
 * @property {'validation' | 'parsing' | 'format'} type - Error type
 */

/**
 * @typedef {Object} ParsedFileData
 * @property {string} fileName - Original file name
 * @property {string} fileType - File type ('csv' or 'txt')
 * @property {number} fileSize - File size in bytes
 * @property {Date} processedAt - When the file was processed
 * @property {FileProcessingResult} result - Processing result
 */

// Default invoice record structure
export const createEmptyInvoiceRecord = () => ({
  id: 0,
  distCode: null,
  invoiceNumber: "",
  createDate: null,
  invoiceDate: null,
  warehouseCode: null,
  customerCode: "",
  customerName: "",
  salesmanCode: null,
  productCode: "",
  productType: "",
  uomConvFactor: null,
  uomCode: null,
  quantity: null,
  netAmount: null,
  promoDiscount: null,
  vatTaxAmount: null,
  totalNetAmount: null,
  isActive: true,
  createdAt: "",
  createdBy: null,
  updatedBy: null,
  updatedAt: "",
});

// CSV column mapping for different file formats
export const CSV_COLUMN_MAPPINGS = {
  standard: {
    createDate: ["create_dt", "createdate"],
    invoiceDate: ["inv_dt", "invoicedate"],
    warehouseCode: ["whs_cd", "warehousecode"],
    customerCode: [
      "cust_cd",
      "customer_code",
      "customer_id",
      "client_code",
      "client_id",
      "customercode",
    ],
    salesmanCode: ["slsman_cd", "salesmancode"],
    productCode: ["prd_cd", "productcode"],
    productType: ["prd_type", "producttype"],
    uomConvFactor: ["uom_conv_factor_sml", "uomconvfactor"],
    uomCode: ["uom_cd", "uomcode"],
    quantity: ["prd_qty"],
    netAmount: ["net_amt", "netamount"],
    promoDiscount: ["prmo_disc", "promodiscount"],
    vatTaxAmount: ["vat_tax_amt", "vattaxamount"],
    totalNetAmount: ["total_net_amt", "totalnetamount"],
    distCode: ["dist_cd", "distributor_code", "distcode"],
    invoiceNumber: [
      "invoice_number",
      "invoice_no",
      "invoice",
      "number",
      "inv_no",
      "invoicenumber",
    ],
    customerName: [
      "customer_name",
      "customer",
      "client_name",
      "client",
      "customername",
    ],
    amount: ["amount", "base_amount", "subtotal", "net_amount"],
    taxRate: ["tax_rate", "vat_rate", "tax_percent", "vat_percent", "taxrate"],
    taxAmount: ["tax_amount", "vat_amount", "tax", "vat", "taxamount"],
    discountAmount: [
      "discount_amount",
      "discount",
      "discount_value",
      "discountamount",
    ],
    totalAmount: [
      "total_amount",
      "total",
      "gross_amount",
      "final_amount",
      "totalamount",
    ],
  },
};

// Validation rules for invoice records
export const VALIDATION_RULES = {
  invoiceNumber: {
    required: true,
    minLength: 1,
    maxLength: 50,
    pattern: /^[A-Za-z0-9\-_]+$/,
  },
  customerName: {
    required: false,
    minLength: 1,
    maxLength: 100,
  },
  amount: {
    required: false,
    min: 0,
    type: "number",
  },
  taxRate: {
    required: false,
    min: 0,
    max: 100,
    type: "number",
  },
  taxAmount: {
    required: false,
    min: 0,
    type: "number",
  },
  totalAmount: {
    required: false,
    min: 0,
    type: "number",
  },
  invoiceDate: {
    required: true,
    type: "date",
  },
};
